import React, { useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CampaignData } from '@/hooks/sms/campaign/types';
import { subDays } from 'date-fns';
import RestaurantSelector from './RestaurantSelector';
import AudienceTypeSelector from './AudienceTypeSelector';
import FilterSection from './FilterSection';
import AudienceSizeIndicator from './AudienceSizeIndicator';

interface AudienceSelectionProps {
  campaignData: CampaignData;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  audienceSize: number;
  audienceLoading?: boolean;
  userRestaurants?: any[];
}

const AudienceSelection: React.FC<AudienceSelectionProps> = ({
  campaignData,
  updateCampaignData,
  audienceSize,
  audienceLoading = false,
  userRestaurants = []
}) => {
  const handleAudienceTypeChange = useCallback((value: 'all' | 'filtered') => {
    updateCampaignData({ audienceType: value });
  }, [updateCampaignData]);

  const handleTotalOrdersChange = useCallback((lowerLimit: number | null, upperLimit: number | null) => {
    updateCampaignData({
      audienceType: 'filtered',
      filters: {
        totalOrders: {
          lowerLimit,
          upperLimit
        }
      }
    });
  }, [updateCampaignData]);

  const handleLastVisitDateChange = useCallback((fromDate: Date | null, toDate: Date | null) => {
    updateCampaignData({
      audienceType: 'filtered',
      filters: {
        lastVisitDate: {
          fromDate,
          toDate
        }
      }
    });
  }, [updateCampaignData]);

  const handleAverageOrderValueChange = useCallback((lowerLimit: number | null, upperLimit: number | null) => {
    updateCampaignData({
      audienceType: 'filtered',
      filters: {
        averageOrderValue: {
          lowerLimit,
          upperLimit
        }
      }
    });
  }, [updateCampaignData]);

  const handleTotalSpendChange = useCallback((lowerLimit: number | null, upperLimit: number | null) => {
    updateCampaignData({
      audienceType: 'filtered',
      filters: {
        totalSpend: {
          lowerLimit,
          upperLimit
        }
      }
    });
  }, [updateCampaignData]);

  const handleLoyaltyTierChange = useCallback((value: string, checked: boolean) => {
    const currentValues = [...(campaignData.filters.loyaltyTier || [])];
    
    if (checked) {
      if (!currentValues.includes(value)) {
        currentValues.push(value);
      }
    } else {
      const index = currentValues.indexOf(value);
      if (index !== -1) {
        currentValues.splice(index, 1);
      }
    }
    
    updateCampaignData({ 
      audienceType: 'filtered',
      filters: {
        loyaltyTier: currentValues
      }
    });
  }, [updateCampaignData, campaignData.filters.loyaltyTier]);

  return (
    <Card 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
    >
      <CardHeader>
        <CardTitle>Select Your Audience</CardTitle>
        <CardDescription>
          Choose which customers will receive your SMS campaign
        </CardDescription>
      </CardHeader>
      <CardContent 
        className="space-y-6"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {userRestaurants && userRestaurants.length > 0 && (
          <RestaurantSelector
            userRestaurants={userRestaurants}
            selectedRestaurants={campaignData.selectedRestaurants}
            updateCampaignData={updateCampaignData}
          />
        )}
        
        <AudienceTypeSelector
          audienceType={campaignData.audienceType}
          onAudienceTypeChange={handleAudienceTypeChange}
        />
        
        {campaignData.audienceType === 'filtered' && (
          <FilterSection
            filters={{
              totalOrders: campaignData.filters.totalOrders || { lowerLimit: 1, upperLimit: null },
              lastVisitDate: campaignData.filters.lastVisitDate || { fromDate: subDays(new Date(), 28), toDate: new Date() },
              averageOrderValue: campaignData.filters.averageOrderValue || { lowerLimit: 0, upperLimit: null },
              totalSpend: campaignData.filters.totalSpend || { lowerLimit: 0, upperLimit: null },
              loyaltyTier: campaignData.filters.loyaltyTier || []
            }}
            onTotalOrdersChange={handleTotalOrdersChange}
            onLastVisitDateChange={handleLastVisitDateChange}
            onAverageOrderValueChange={handleAverageOrderValueChange}
            onTotalSpendChange={handleTotalSpendChange}
            onLoyaltyTierChange={handleLoyaltyTierChange}
          />
        )}
        
        <AudienceSizeIndicator 
          audienceSize={audienceSize}
          audienceLoading={audienceLoading}
        />
      </CardContent>
    </Card>
  );
};

export default AudienceSelection;